'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';

interface Photo {
  src: string;
  alt: string;
  caption: string;
  rotation: number;
}

interface ScrapbookPhotosProps {
  photos: Photo[];
  title?: string;
}

export default function ScrapbookPhotos({ photos, title }: ScrapbookPhotosProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  return (
    <section className="py-16">
      {title && (
        <h2 className="text-3xl font-bold text-gray-900 border-b-2 border-primary-200 pb-2 mb-8 text-center">
          {title}
        </h2>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {photos.map((photo, index) => (
          <div
            key={index}
            className="group relative scrapbook-photo hover:scale-102 hover:rotate-0.5 hover:-translate-y-0.5"
            style={{
              transform: `rotate(${photo.rotation}deg)`,
            }}
          >
            {/* Polaroid-style photo container */}
            <div className="bg-white p-3 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
              {/* Photo */}
              <div className="relative overflow-hidden rounded-sm">
                <Image
                  src={photo.src}
                  alt={photo.alt}
                  width={300}
                  height={300}
                  className="w-full h-64 object-cover transform transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              
              {/* Caption */}
              <div className="mt-3 text-center">
                <p 
                  className="text-gray-700 font-medium text-sm leading-tight"
                  style={{
                    fontFamily: "'Caveat', cursive, sans-serif",
                    fontSize: '1.1rem',
                    lineHeight: '1.2'
                  }}
                >
                  {photo.caption}
                </p>
              </div>
            </div>
            
            {/* Decorative elements for extra scrapbook feel */}
            <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-200 rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-pink-200 rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        ))}
      </div>
    </section>
  );
}
